/**
 * PickupMenu용 API 데이터 훅
 * API 메뉴 데이터를 PickupMenu 형식으로 변환
 *
 * #194: 메뉴 API에서 매장 상세 정보를 함께 반환하므로
 *       별도의 매장 API 호출이 불필요해짐 (2회 → 1회)
 * #907: 매장별 세율/통화를 StorePricingContext에 설정
 */
import { useMemo, useEffect } from 'react';
import { useStoreMenus } from './useStoreMenus';
import type { MenuItem, MenuCategoryItem, Store as LocalStore } from '@/data';
import { transformApiStoreToLocal } from '@/lib/api';
import type { MenuOption as ApiMenuOption } from '@/lib/api';
import type { ApiMenuOptionGroup, MenuAvailabilityInfo, MenuDiscount } from '@/lib/api/types';
import { useApp } from '@/contexts/AppContext';
import { useStorePricing } from '@/contexts/StorePricingContext';
import { menuTranslations } from '@/i18n';
import { logger } from '@/utils/logger';
import placeholderMenu from '@/assets/placeholder-menu.webp';

interface UsePickupMenuDataReturn {
  menuItems: MenuItem[];
  categories: MenuCategoryItem[];
  loading: boolean;
  error: Error | null;
  isApiData: boolean;
  storeName: string | null;
  /** API 매장 상세 정보를 로컬 Store 형식으로 변환한 데이터 */
  storeInfo: LocalStore | null;
  /** 조직 ID (동일 조직 매장 조회용) */
  organizationId: number | null;
  /** 브랜드 ID (동일 브랜드 매장 조회용) */
  brandId: number | null;
  /** API 매장 ID (숫자, API 호출용) */
  storeApiId: number | null;
}

/**
 * API MenuOption을 ApiMenuOptionGroup 형식으로 변환
 */
export function transformApiMenuOption(apiOption: ApiMenuOption): ApiMenuOptionGroup {
  // API type과 is_required로 input_type 결정. 입력형 옵션은 선택형으로 재분류하지 않고 보존한다.
  let inputType: ApiMenuOptionGroup['input_type'];
  if (apiOption.type === 'text_input' || apiOption.type === 'number_input' || apiOption.type === 'textarea') {
    inputType = apiOption.type;
  } else if (apiOption.type === 'multiple' || (apiOption.max_selections && apiOption.max_selections > 1)) {
    inputType = apiOption.is_required ? 'required_multiple' : 'multiple';
  } else {
    inputType = apiOption.is_required ? 'required_single' : 'single';
  }

  return {
    id: apiOption.id,
    name: apiOption.name,
    description: null,
    input_type: inputType,
    is_required: apiOption.is_required,
    min_select: apiOption.min_selections ?? (apiOption.is_required ? 1 : 0),
    max_select: apiOption.max_selections ?? null,
    sort_order: 0,
    options: apiOption.options.map(choice => ({
      id: choice.id,
      name: choice.name,
      description: null,
      additional_price: choice.price,
      quantity_limit: choice.quantity_limit,
      is_available: true,
      sort_order: 0,
    })),
  };
}

/**
 * API Menu를 로컬 MenuItem 형식으로 변환
 */
function transformApiMenuToMenuItem(apiMenu: {
  id: number;
  name: string;
  description: string | null;
  price: number;
  original_price?: number | null;
  discount?: MenuDiscount | null;
  category: string | null;
  category_id: number | null;
  image_url: string | null;
  is_available: boolean;
  is_sold_out?: boolean;
  is_available_at?: MenuAvailabilityInfo;
  is_popular?: boolean;
  is_set_menu?: boolean;
  set_menu_items?: { id: number }[];
  options?: ApiMenuOption[];
  store_id: number;
  /** #1439 G4 — 영업시간 외 표시 모드 */
  display_state?: 'hidden' | 'disabled' | 'active';
}): MenuItem {
  // store_id가 null인 경우 명확한 에러 발생
  if (apiMenu.store_id == null) {
    throw new Error(`[API 변환] ApiMenu store_id 누락: ${apiMenu.id}`);
  }

  // category_id가 null이면 -1 (미분류)로 처리
  const categoryId = apiMenu.category_id ?? -1;

  // BE 계약: `MenuResource`는 `finalOriginalPrice > discountedPrice`인 경우에만
  //          `original_price`를 채움. 0 이하는 BE 버그/오염 데이터로 간주하여
  //          폐기(undefined) — `hasDiscount(item)`이 0을 할인으로 오인하지 않도록.
  let originalPrice: number | undefined;
  if (apiMenu.original_price != null && apiMenu.original_price > 0) {
    originalPrice = apiMenu.original_price;
  } else if (apiMenu.original_price != null) {
    logger.warn('[usePickupMenuData] BE original_price 비정상 값 폐기', {
      menuId: apiMenu.id,
      value: apiMenu.original_price,
    });
  }

  return {
    id: String(apiMenu.id),
    nameKey: `api_menu_${apiMenu.id}`,
    descriptionKey: `api_menu_${apiMenu.id}`,
    price: apiMenu.price,
    category: String(categoryId),
    imageUrl: apiMenu.image_url || placeholderMenu,
    isAvailable: apiMenu.is_available,
    isSoldOut: apiMenu.is_sold_out ?? false,
    availability: apiMenu.is_available_at,
    // #1439 G4 — 영업시간 외 표시 모드 (BE display_state 매핑).
    displayState: apiMenu.display_state,
    isPopular: apiMenu.is_popular ?? false,
    originalPrice,
    // #281: `isDiscounted` 플래그 제거. 할인 판정은 소비처에서 `hasDiscount(item)`로 단일화.
    //       `discount`는 BE 응답을 그대로(없으면 undefined) 보존하여 두 필드 불일치를 차단.
    discount: apiMenu.discount ?? undefined,
    // API의 store_id를 restaurantId로 매핑
    restaurantId: String(apiMenu.store_id),
    // API 직접 이름 저장
    _name: apiMenu.name,
    _description: apiMenu.description || '',
    // API 옵션 저장
    _options: apiMenu.options?.map(transformApiMenuOption),
    // 세트 메뉴 정보
    isSetMenu: apiMenu.is_set_menu || false,
    setMenuItemCount: apiMenu.set_menu_items?.length || 0,
  };
}

/**
 * API 카테고리를 로컬 MenuCategoryItem 형식으로 변환
 */
function transformApiCategory(apiCategory: {
  id: number;
  name: string;
  description: string;
  display_order: number;
  is_available: boolean;
}): MenuCategoryItem {
  return {
    id: String(apiCategory.id),
    nameKey: `api_category_${apiCategory.id}`,
    _name: apiCategory.name,
  };
}

/**
 * PickupMenu용 데이터 훅
 * @param storeId 매장 ID (string 또는 number)
 *
 * #194: 메뉴 API 응답에 매장 상세 정보가 포함되므로
 *       별도의 매장 API 호출 불필요 (API 호출 1회 감소)
 * #907: 매장 로드 시 StorePricingContext에 세율/통화 설정
 */
export function usePickupMenuData(storeId: string | undefined): UsePickupMenuDataReturn {
  // #194: 메뉴 API에서 매장 상세 정보도 함께 반환
  const { store, categories: apiCategories, menus: apiMenus, loading, error } = useStoreMenus(storeId);
  const { locale } = useApp();
  const t = menuTranslations[locale];

  // #907: 매장별 세율/통화 설정
  const { setStorePricing } = useStorePricing();

  // 매장 로드 시 세율/통화/포인트 정책 설정
  //
  // #285 P1 대응: cleanup 에서 clearStorePricing 호출을 제거.
  // 기존 cleanup 은 /store/:id → /checkout 라우트 전환 시 즉시 실행돼 체크아웃 화면에서
  // pointsEnabled 가 항상 false 가 되는 회귀를 일으킴. 다음 매장 진입 시 새 store effect 가
  // 값을 덮어쓰므로 cleanup 없이도 stale 문제는 발생하지 않음.
  // (store 가 null 일 때는 이전 매장 값이 유지되지만, store 없는 페이지(홈/주문내역)
  //  는 세율/통화/포인트 UI 를 노출하지 않아 실사용 영향 없음.)
  useEffect(() => {
    if (store) {
      // API 응답의 tax_rate는 퍼센트(16), transformers에서 비율(0.16)로 변환됨
      // storeInfo.taxRate는 이미 비율 형태
      // #285: store.points 는 BE PR #1142 배포 전이면 undefined → normalize 가 null 반환
      setStorePricing(
        store.tax_rate != null ? store.tax_rate / 100 : null,
        store.currency,
        store.points ?? null
      );
    }
  }, [store, setStorePricing]);

  // API 데이터를 로컬 형식으로 변환
  // 목업 데이터 폴백 제거 - API 결과만 사용
  const { menuItems, categories, isApiData } = useMemo(() => {
    const transformedMenus = apiMenus.map(transformApiMenuToMenuItem);

    // 중복 카테고리 제거 (API가 중복 반환하는 경우 처리)
    const uniqueCategories = apiCategories.reduce((acc, cat) => {
      if (!acc.find(c => c.id === cat.id)) {
        acc.push(cat);
      }
      return acc;
    }, [] as typeof apiCategories);

    let transformedCategories = uniqueCategories.map(transformApiCategory);

    // 방어적 코드: 미분류 메뉴가 있지만 미분류 카테고리가 없는 경우 추가
    const hasUncategorizedMenus = transformedMenus.some(m => m.category === '-1');
    const hasUncategorizedCategory = transformedCategories.some(c => c.id === '-1');

    if (hasUncategorizedMenus && !hasUncategorizedCategory) {
      transformedCategories = [
        ...transformedCategories,
        {
          id: '-1',
          nameKey: 'uncategorized',
          _name: t.categories.uncategorized,
        },
      ];
    }

    // #230 AD1 — categories 빈 배열 + 메뉴 존재 시 "전체" 가상 카테고리 fallback.
    // BE 가 카테고리 미반환 또는 카테고리 미부여 매장에서 메뉴만 표시할 수 있도록 보장.
    if (transformedCategories.length === 0 && transformedMenus.length > 0) {
      transformedCategories = [
        {
          id: 'all',
          nameKey: 'all',
          _name: t.categories.all,
        },
      ];
    }

    return {
      menuItems: transformedMenus,
      categories: transformedCategories,
      isApiData: apiMenus.length > 0,
    };
  }, [apiMenus, apiCategories, t.categories.uncategorized, t.categories.all]);

  // #194: 메뉴 API 응답의 매장 정보 사용 (별도 API 호출 불필요)
  // #61, #62: 폴백 로직 제거 - API 실패 시 null 반환
  const storeInfo = useMemo(() => {
    if (store) {
      return transformApiStoreToLocal(store);
    }
    // API 실패 시 null 반환 (로딩/에러 상태로 처리)
    if (!loading && storeId) {
      console.warn(`[usePickupMenuData] 매장 정보 조회 실패: storeId=${storeId}`);
    }
    return null;
  }, [store, storeId, loading]);

  // #194: 조직 ID도 메뉴 API 응답에서 추출
  const organizationId = store?.organization?.id || null;

  // #249: 브랜드 ID도 메뉴 API 응답에서 추출 (동일 브랜드 매장 조회용)
  const brandId = store?.brand?.id || null;

  // API 매장 ID (숫자)
  const storeApiId = store?.id || null;

  return {
    menuItems,
    categories,
    loading,
    error,
    isApiData,
    storeName: store?.name || null,
    storeInfo,
    organizationId,
    brandId,
    storeApiId,
  };
}

/**
 * API 메뉴용 동적 번역 생성
 */
export function createMenuTranslations(
  menuItems: MenuItem[]
): Record<string, { name: string; description: string }> {
  const translations: Record<string, { name: string; description: string }> = {};

  for (const item of menuItems) {
    if (item._name) {
      translations[item.nameKey] = {
        name: item._name,
        description: item._description || '',
      };
    }
  }

  return translations;
}

/**
 * API 카테고리용 동적 번역 생성
 */
export function createCategoryTranslations(
  categories: MenuCategoryItem[]
): Record<string, string> {
  const translations: Record<string, string> = {};

  for (const cat of categories) {
    if (cat._name) {
      translations[cat.nameKey] = cat._name;
    }
  }

  return translations;
}
